import { z } from 'zod';
import { HttpClient } from '@sisense/sdk-rest-client';

/**
 * Canonical tool description for uploadFiles — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const uploadFilesToolDescription = `Opens the host's file-upload flow so the user can provide one or more CSV files. When the host uses a blocking UI, this should be the ONLY tool call in that turn until the user completes the upload — do not chain other tools in parallel.

Use when the user wants to upload, ingest, import, or onboard raw files into a new Elasticube model.

Onboarding pace: start this step alone; after files are uploaded, agree on a model title with the user in chat before calling buildDataModelFromUploads — do not rush multiple structural steps in one assistant turn when the workflow expects the user to confirm between steps.

After this tool returns successfully, propose a model title derived from the file names and ask the user to confirm or change it before calling buildDataModelFromUploads.`;

/**
 * Canonical tool description for buildDataModelFromUploads — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const buildDataModelFromUploadsToolDescription = `Builds a complete Sisense data model (Elasticube) from previously uploaded CSV files. Auto-detects each file's column schema, creates the Elasticube, adds one dataset containing all files, infers FK relations by matching column names (e.g. ArtistId → Artist.ArtistId), and triggers the full build. Polls until the build is done or fails.

Returns datamodelOid, title, tables (with oids and columns), relations, and buildStatus.

Call only after uploadFiles returned successfully AND the user confirmed they want to build the model. Pass the exact files array from the uploadFiles result as uploadedFiles — never invent paths or file names. Default inferRelations to true unless the user explicitly declines relation inference.

Do not bundle unrelated structural modeling actions in the same assistant turn as this call when a normal workflow would pause for user confirmation between steps.

After this tool succeeds, you already have the new datamodelOid and table structure in the result — skip getDataSources and getModelSchema before proposing perspectives; base perspective ideas on the returned table names.`;

// ─── uploadFiles ─────────────────────────────────────────────────────────────

export const uploadFilesParamsSchema = z
	.object({
		accept: z
			.array(z.enum(['csv']))
			.optional()
			.describe('Restrict file types; default ["csv"]'),
		maxFiles: z
			.number()
			.int()
			.positive()
			.optional()
			.describe('Max number of files; default unlimited'),
	})
	.passthrough();

export interface UploadedFile {
	originalName: string;
	fileName: string;
	storagePath: string;
	size: number;
}

export interface UploadFilesResult {
	files: UploadedFile[];
}

// ─── buildDataModelFromUploads ───────────────────────────────────────────────

// .passthrough() preserves any extra fields the LLM might echo back from the
// uploadFiles result (e.g. `size`). Without this, zod strips unknown keys, the
// parsed args no longer deep-equal the raw arguments string, and getToolCallId
// fails to find the call — surfacing as "No tool call id found".
export const uploadedFileSchema = z
	.object({
		originalName: z.string(),
		fileName: z.string(),
		storagePath: z.string(),
		size: z.number().optional(),
	})
	.passthrough();

export const buildCsvModelParamsSchema = z
	.object({
		uploadedFiles: z
			.array(uploadedFileSchema)
			.min(1)
			.describe(
				'Files previously uploaded via the uploadFiles tool. Use the exact array returned in that tool result — never invent paths.',
			),
		modelTitle: z.string().min(1).describe('Title for the new Elasticube'),
		inferRelations: z
			.boolean()
			.describe(
				'Auto-detect FK relations by matching column names (e.g. ArtistId → Artist.ArtistId). Pass true unless the user explicitly says no relations.',
			),
	})
	.passthrough();

export interface BuiltTable {
	oid: string;
	name: string;
	datasetOid: string;
	columns: Array<{ oid: string; name: string; type: number }>;
}

export interface BuiltRelation {
	oid: string;
	sourceTable: string;
	sourceColumn: string;
	targetTable: string;
	targetColumn: string;
}

export interface BuildCsvModelResult {
	datamodelOid: string;
	title: string;
	tables: BuiltTable[];
	relations: BuiltRelation[];
	buildOid: string;
	buildStatus: 'done' | 'failed' | 'timeout';
	buildDurationMs: number;
}

// ─── Shared engine context ────────────────────────────────────────────────────

export interface CsvModelEngineContext {
	toolCallId: string;
	httpClient?: HttpClient;
}
